import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Activity, LogIn, LogOut, Menu, Settings, Swords, UserRound } from "lucide-react";
import { cn } from "../../lib/cn";
import { fr } from "../../i18n/fr";
import { useAuth } from "../../lib/auth";
import { useOnline } from "../../lib/publicHooks";
import { Button, PlayerHead } from "../ui";
import { Logo } from "./Logo";
import { NavMenu } from "./NavMenu";
import { MobileDrawer } from "./MobileDrawer";
import { PUBLIC_MENUS, CLIENT_NAV, ADMIN_NAV, filterNav } from "./navModel";

export function Header() {
  const { isAuthenticated, isStaff, isLinked, user, hasRole, logout } = useAuth();
  const online = useOnline();
  const [mobileOpen, setMobileOpen] = useState(false);

  const ctx = { authenticated: isAuthenticated, linked: isLinked, hasRole };
  const clientLinks = filterNav(CLIENT_NAV, ctx);
  const adminLinks = filterNav(ADMIN_NAV, ctx);

  const extraSections = [
    isAuthenticated && clientLinks.length
      ? { id: "espace", label: fr.nav.panel, icon: <UserRound className="h-4 w-4" />, links: clientLinks }
      : null,
    isStaff && adminLinks.length
      ? { id: "admin", label: fr.nav.admin, icon: <Settings className="h-4 w-4" />, links: adminLinks }
      : null,
  ].filter(Boolean) as { id: string; label: string; icon: ReactNode; links: typeof clientLinks }[];

  return (
    <header className="forge-header sticky top-0 z-40 border-b border-iron-300/50 bg-obsidian-500 shadow-header">
      <div className="safe-x mx-auto flex h-[72px] max-w-[1500px] items-center gap-3">
        <Logo className="shrink-0 pr-4" />

        <nav aria-label="Navigation principale" className="hidden h-full min-w-0 flex-1 border-l border-iron-400/50 lg:block">
          <ul className="flex h-full items-stretch">
            {PUBLIC_MENUS.map((menu) => (
              <NavMenu key={menu.id} id={menu.id} label={menu.label} links={menu.links} {...(menu.to ? { to: menu.to } : {})} />
            ))}
          </ul>
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <OnlinePill online={online.data?.online} max={online.data?.max} />

          <nav aria-label="Espaces personnels" className="hidden h-full xl:block">
            <ul className="flex h-full items-stretch">
              {isAuthenticated && clientLinks.length > 0 && (
                <NavMenu id="espace" label={fr.nav.panel} to="/panel" align="right" links={clientLinks} />
              )}
              {isStaff && adminLinks.length > 0 && (
                <NavMenu id="admin" label={fr.nav.admin} to="/admin" align="right" links={adminLinks} />
              )}
            </ul>
          </nav>

          {isAuthenticated ? (
            <div className="hidden items-center gap-2 lg:flex">
              <Link
                to="/panel"
                className="forge-player-cta flex h-11 items-center gap-2 border border-blood-300/70 bg-blood-500 px-3 text-parchment-50 shadow-glow-blood transition-colors hover:bg-blood-400"
                aria-label={user?.name ?? "Mon espace"}
              >
                <PlayerHead uuid={user?.uuid} name={user?.name} size={24} rounded />
                <span className="max-w-[8rem] truncate text-sm font-semibold">{user?.name ?? user?.email ?? "Joueur"}</span>
              </Link>
              <button
                type="button"
                className="grid h-11 w-11 place-items-center border border-iron-300/50 bg-obsidian-100 text-parchment-200/70 transition-colors hover:border-arcane-300/60 hover:text-arcane-100"
                onClick={() => void logout()}
                aria-label={fr.auth.deconnexion}
                title={fr.auth.deconnexion}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="hidden items-center gap-2 lg:flex">
              <Link to="/connexion">
                <Button variant="secondary" size="sm" iconLeft={<LogIn className="h-4 w-4" />}>{fr.auth.connexion}</Button>
              </Link>
              <Link to="/inscription">
                <Button variant="danger" size="sm" iconLeft={<Swords className="h-4 w-4" />}>{fr.auth.inscription}</Button>
              </Link>
            </div>
          )}

          <button
            className="tap-target inline-flex items-center justify-center border border-iron-300/50 bg-obsidian-100 p-2 text-parchment-100 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        menus={PUBLIC_MENUS}
        extraSections={extraSections}
        footer={
          isAuthenticated ? (
            <div className="flex flex-col gap-2 min-[380px]:flex-row">
              <Link to="/panel" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button variant="danger" size="sm" fullWidth iconLeft={<PlayerHead uuid={user?.uuid} name={user?.name} size={18} rounded />}>
                  {fr.nav.panel}
                </Button>
              </Link>
              <Button variant="secondary" size="sm" fullWidth iconLeft={<LogOut className="h-4 w-4" />} onClick={() => void logout()}>
                {fr.auth.deconnexion}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 min-[380px]:flex-row">
              <Link to="/connexion" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button variant="secondary" size="sm" fullWidth>{fr.auth.connexion}</Button>
              </Link>
              <Link to="/inscription" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button variant="danger" size="sm" fullWidth>{fr.auth.inscription}</Button>
              </Link>
            </div>
          )
        }
      />
    </header>
  );
}

function OnlinePill({ online, max }: { online?: number; max?: number }) {
  const isUp = online !== undefined;
  return (
    <div className="hidden h-11 items-center gap-2 border border-iron-300/50 bg-obsidian-100 px-3 md:flex" title={fr.home.joueursEnLigne}>
      <Activity className={cn("h-4 w-4", isUp ? "text-emerald-300" : "text-parchment-200/30")} />
      <span className="font-mono text-sm text-parchment-100">
        {isUp ? online : "-"}{max ? <span className="text-parchment-200/40">/{max}</span> : null}
      </span>
      <span className="hidden text-[10px] uppercase tracking-wider text-emerald-300 lg:block">en ligne</span>
    </div>
  );
}
